package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import com.swetdot.swetgram.domain.GroupBanEntry
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.ui.chat.render
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetListRowSkeleton
import com.swetdot.swetgram.ui.design.SwetTextButton
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.profile.ProfileConfirmDialog
import kotlinx.datetime.TimeZone

/**
 * A group's banned people, and the only place a ban can be lifted.
 *
 * NO DESIGN EXISTS FOR THIS SCREEN either -- SPS §12.7 has no ban row and STA §13.5 no ban
 * endpoint -- so the layout is a DECISION, and it is deliberately `BlockedUsersScreen`'s: the same
 * row anatomy, the same trailing text button, the same non-destructive confirmation. The two
 * screens do the same job at different scopes, one for a person and one for a group, and somebody
 * who has used the first should not have to learn the second.
 *
 * WHAT THE ROW SAYS, AND WHY IT NAMES THE ADMIN. `GroupBan` carries `banned_by`, so the row says
 * who did it. That is not gossip: this is a shared moderation surface where a second admin decides
 * whether to undo a colleague's decision, and "banned by Ada" is the difference between undoing
 * something deliberate and undoing something accidental. The person who did it may no longer be in
 * the group at all -- the server keeps the reference alive precisely so the answer survives their
 * departure.
 *
 * THERE IS NO REASON AND NO EXPIRY, and none is invented. `GroupBan` has three properties: the
 * user, who banned them, and when.
 *
 * UNBANNING IS CONFIRMED but not painted as destructive, exactly as unblocking is: it is
 * reversible, it is the safety-REDUCING direction, and in a list where every row looks the same an
 * accidental tap silently re-opens a door somebody closed on purpose.
 */
@Composable
fun GroupBansScreen(
    state: GroupBansUiState,
    onBack: () -> Unit,
    /** `unbanGroupMember`. The ViewModel re-checks the permission before sending it. */
    onUnban: (UserId) -> Unit,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    /** Null disables paging -- for a caller that shows a single page on purpose. */
    onLoadMore: (() -> Unit)? = null,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    var pendingUnban by remember { mutableStateOf<GroupBanEntry?>(null) }

    Column(modifier.fillMaxSize()) {
        // TODO(localization): every user-facing string in this file moves into resources (EN + RU).
        SwetTopAppBar(title = "Banned people", onBack = onBack)

        // Over the list, never in place of it: a failed page must not empty the rows that did
        // load, because those rows are the only way to lift a ban.
        if (state.error != null) {
            SwetErrorBanner(message = state.error, onRetry = onRetry, onDismiss = onDismissError)
        }

        when {
            // First load. Skeleton rows rather than a spinner (SDS §2.1).
            state.bans.isEmpty() && !state.loadedOnce -> Column(Modifier.fillMaxSize()) {
                repeat(SKELETON_ROWS) { SwetListRowSkeleton() }
            }

            // §17.1's voice: short, helpful, calm. The second line says where entries come from,
            // because this list is empty for most groups and an admin should know what fills it.
            state.bans.isEmpty() -> SwetEmptyState(
                title = "Nobody is banned.",
                body = "People you ban from the members list appear here, and this is where you " +
                    "can let them back in.",
            )

            else -> LazyColumn(Modifier.fillMaxSize().navigationBarsPadding()) {
                items(
                    items = state.bans,
                    // The user id, not the position: an unban takes a row out of the middle, and a
                    // positional key would animate the wrong one away.
                    key = { it.user.id.value },
                ) { ban ->
                    GroupPersonRow(
                        user = ban.user,
                        // Who and when, in one line. The date is rendered with the same formatter
                        // the timeline and the blocked list use, and the whole clause is dropped
                        // rather than faked when the date could not be parsed -- the row still has
                        // to appear, because it is the only way to undo this.
                        detail = banDetail(
                            bannedBy = ban.bannedBy.displayName,
                            on = ban.createdAt?.let { LocalDay.of(it, zone).render() },
                        ),
                    ) {
                        SwetTextButton(
                            text = "Unban",
                            onClick = { pendingUnban = ban },
                            // Momentary, which is the case SDS §25.1's disabled state is for.
                            enabled = state.canUnban && state.unbanning == null,
                        )
                    }
                }

                if (state.hasMore) {
                    item(key = PAGING_FOOTER_KEY) {
                        // Keyed on the item count so it fires once per page rather than on every
                        // recomposition -- and NOT again after a failed page, since the count did
                        // not change. The banner's Retry is the way back.
                        LaunchedEffect(state.bans.size) { onLoadMore?.invoke() }
                        SwetListRowSkeleton()
                    }
                }
            }
        }
    }

    pendingUnban?.let { ban ->
        ProfileConfirmDialog(
            title = "Unban ${ban.user.displayName}?",
            // The sentence that stops "unban" reading as "undo". Lifting a ban does not put them
            // back: no operation in the contract adds somebody to a group without their asking, so
            // they have to use a link or ask again -- and when they do they start from now.
            body = "They are not added back to the group. They will be able to use an invite " +
                "link or ask to join again, and they will not see anything sent before they do.",
            confirmText = "Unban",
            onConfirm = {
                pendingUnban = null
                onUnban(ban.user.id)
            },
            onDismiss = { pendingUnban = null },
            // Reversible, and the safety-reducing direction -- so it is confirmed, but it is not
            // painted as a deletion. The same reading `BlockedUsersScreen` gives unblocking.
            destructive = false,
        )
    }
}

/** Enough placeholder rows to fill the first screen without pretending to know the real count. */
private const val SKELETON_ROWS = 5
private const val PAGING_FOOTER_KEY = "group-bans-paging-footer"

/**
 * "Banned by Ada Lovelace on 03.08.2026", or just the name when the date could not be read.
 *
 * Never null: `banned_by` is required by the contract, so there is always something true to say,
 * and a row on this list without a "banned by" would be the one entry nobody could account for.
 *
 * Separated out so the join of the two pieces is testable without a list around it.
 */
// TODO(localization): these strings move into resources for EN + RU.
internal fun banDetail(bannedBy: String, on: String?): String =
    if (on == null) "Banned by $bannedBy" else "Banned by $bannedBy on $on"
